import { LockOutlined } from '@ant-design/icons';
import { Result, Spin } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useModules } from '../contexts/ModulesContext';
import {
    moduleTitleKey,
    moduleUnavailableMessageKey,
    moduleUnavailableReason,
} from '../domain/modules';

interface ModuleGuardProps {
    moduleKey: string;
    children: ReactNode;
}

/**
 * Renders a module's page only when the module is available.
 *
 * The menu already greys the entry out, but a URL can be typed or bookmarked. Without this the page
 * would mount and fire requests the backend refuses with 403; with it, nothing of the module runs
 * and the user is told why.
 */
export default function ModuleGuard({ moduleKey, children }: Readonly<ModuleGuardProps>) {
    const { t } = useTranslation();
    const { modules, loaded } = useModules();

    if (!loaded) {
        return <Spin data-testid="module-guard-loading" />;
    }

    const reason = moduleUnavailableReason(modules, moduleKey);

    if (reason === null) {
        return children;
    }

    // The test id sits on a wrapper: `Result` does not pass unknown props on to its DOM.
    return (
        <div data-testid="module-unavailable">
            <Result
                icon={<LockOutlined aria-hidden />}
                title={t('modules.unavailableTitle', { module: t(moduleTitleKey(moduleKey)) })}
                subTitle={t(moduleUnavailableMessageKey(reason))}
            />
        </div>
    );
}
